/**
 * IHS-8 Over goals — per-team Over X.X hit rates.
 *
 * COMPACTION 2026-07-31 (Stat Row v2 / compact-by-default law): the pair
 * of 56px circular gauges per line gave way to plain "62%" values + the
 * mirrored centre-out share bar (`renderType='bar'`) — each line went
 * ~64px → ~38px, so a four-line card fits in the space two gauge lines
 * used to take. Gauges remain reserved for genuinely three-part visuals.
 *
 * The line label is composed render-side from the locale-neutral `key`
 * (e.g. `over-1.5`) via the i18n key `ihs.overGoals.line` with `{{line}}`
 * interpolation, so "Over 1.5" / "Över 1.5" / "Over 1,5" all flow from
 * the same engine payload. Falls back to the engine-shipped `label` if
 * the key can't be parsed.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import type {
  IhsOverGoalsData,
  IhsOverGoalsLine,
} from '../../../types/ihs.js';
import { IhsCard } from './IhsCard.js';
import { StatComparisonRow } from '../../primitives/StatComparisonRow.js';
import { LegendRow } from '../../primitives/LegendRow.js';
import { TEAM_HOME_COLOR, TEAM_AWAY_COLOR } from '../../../styles/team-colors.js';
import styles from './IhsOverGoals.module.css';

const HOME_COLOR = TEAM_HOME_COLOR;
const AWAY_COLOR = TEAM_AWAY_COLOR;

/**
 * Parse a stable line key like `over-1.5` → numeric `1.5`. Returns null
 * for unrecognised shapes so the engine `label` can act as fallback.
 */
function lineValueFromKey(key: string): number | null {
  const match = /^over-(\d+(?:\.\d+)?)$/.exec(key);
  if (!match) return null;
  const n = Number.parseFloat(match[1] ?? '');
  return Number.isFinite(n) ? n : null;
}

/** 0..1 ratio → "62%" (or em-dash when the engine has no value). */
function pct(ratio: number | null): string {
  return ratio === null ? '—' : `${Math.round(ratio * 100)}%`;
}

/** Leader emphasis — higher hit-rate leads; equal = 'both'; missing → plain. */
function pctLead(
  home: number | null,
  away: number | null,
): 'home' | 'away' | 'both' | undefined {
  if (home === null || away === null) return undefined;
  if (home === away) return 'both';
  return home > away ? 'home' : 'away';
}

function LineRow({
  line,
  formatLabel,
}: {
  line: IhsOverGoalsLine;
  formatLabel: (line: IhsOverGoalsLine) => string;
}): ReactElement {
  const lead = pctLead(line.homePct, line.awayPct);
  return (
    <StatComparisonRow
      label={formatLabel(line)}
      home={pct(line.homePct)}
      away={pct(line.awayPct)}
      renderType="bar"
      {...(lead !== undefined ? { lead } : {})}
      animateOnInView
    />
  );
}

export function IhsOverGoals({
  data,
}: {
  data: IhsOverGoalsData;
}): ReactElement {
  const { t } = useTranslation();
  const title =
    data.header?.title ??
    t('ihs.overGoals.title', { defaultValue: 'Over X.X Goals' });

  const formatLabel = (line: IhsOverGoalsLine): string => {
    const value = lineValueFromKey(line.key);
    if (value === null) return line.label;
    return t('ihs.overGoals.line', {
      line: value,
      defaultValue: `Over ${value} Goals`,
    });
  };

  return (
    <IhsCard
      title={title}
      {...(data.header?.subtitle !== undefined
        ? { subtitle: data.header.subtitle }
        : {})}
    >
      <div className={styles.legendWrap}>
        <LegendRow
          items={[
            { label: data.home.name, color: HOME_COLOR },
            { label: data.away.name, color: AWAY_COLOR, position: 'right' },
          ]}
        />
      </div>
      <div className={styles.lines}>
        {data.lines.map((line) => (
          <LineRow key={line.key} line={line} formatLabel={formatLabel} />
        ))}
      </div>
    </IhsCard>
  );
}
